import {
    RESET_SEARCH,
    SET_COMMON_CATEGORIES,
    SET_IS_LOADING_MORE,
    SET_IS_SEARCHING,
    SET_SEARCH_FUZZY,
    SET_SEARCH_IS_ACTIVE,
    SET_SEARCH_IS_INACTIVE,
    SET_SEARCH_LIMIT,
    SET_SEARCH_OFFSET,
    SET_SEARCH_RESULT,
    SET_SEARCH_TERM,
} from 'reducers/actions-types';

import { reduce } from 'lodash';
import axios from 'axios';
import { search } from 'constants/api-urls';
import { dispatchError } from 'reducers/helpers';
import tracking from 'tracking';
import { getCategoriesTrackingData, getProductsTrackingData } from './helpers';
import { SEARCH_KEYWORD_MIN_LENGTH } from 'constants/search';
import { getCooperativeBySlug } from 'util/misc/get-region';
import productsFactoryMiddleware from 'util/misc/products-factory-middleware';
import emojiTranslator from 'emoji-translator';
import { emojis } from 'constants/emojis-to-german';

const flattedEmojis = reduce(emojis, (acc, value) => {
    acc[value.emoji] = value.text;
    return acc;
}, {});

const emojiReplacer = emojiTranslator(flattedEmojis);

let queue = [];

/**
 * Get axios request token
 * @param {string} key
 * @return {CancelToken}
 */
const getAxiosToken = key => {
    let a = queue.filter(obj => {
        return obj.id === key;
    });
    return a[0].source.token;
};

const cancelPendingRequests = () => {
    queue.map(req => {
        req.source.cancel('New search request inbound, clearing queue');
        return req;
    });
    queue = [];
};

/**
 * Search query
 * @param {string} term
 * @param {boolean} loadMore
 * @param {boolean} isEnterPressed
 * @return {function: Promise<ISearch | never>}
 */
const query = ({ term, loadMore, isEnterPressed }) => {
    return function(dispatch, getState) {
        const { offset, limit, isFuzzy } = getState().search;
        /** @type {string} */
        const cooperative = getCooperativeBySlug(getState().region.active);

        /** @type {string[]} */
        const words = emojiReplacer(term);

        if (words.length < SEARCH_KEYWORD_MIN_LENGTH) {
            cancelPendingRequests();
            dispatch(reset());
            return null;
        }

        if (!loadMore && !isEnterPressed) {
            cancelPendingRequests();
            dispatch(reset());
        }

        let token = axios.CancelToken;

        queue.push({
            id: words,
            source: token.source(),
        });

        if (loadMore) {
            dispatch({ type: SET_IS_LOADING_MORE, isLoadingMore: true });
        }

        dispatch({ type: SET_IS_SEARCHING, isSearching: true });

        return axios
            .get(search(words, cooperative), {
                cancelToken: getAxiosToken(words),
                params: {
                    offset: offset,
                    limit: limit,
                    fuzzy: isFuzzy,
                },
            })
            .then(
                /**
                 *  @param {ISearch} data
                 *  @return {ISearch}
                 */
                ({ data }) => {
                    if (data.products) {
                        data.products.items = productsFactoryMiddleware(data.products.items, { cooperative });
                    }
                    return data;
                })
            .then(
                /**
                 * @param {ISearch} data
                 * @return {ISearch}
                 */
                data => {
                    dispatch({ type: SET_SEARCH_TERM, term });
                    dispatch({ type: SET_SEARCH_RESULT, result: data });
                    dispatch({ type: SET_IS_SEARCHING, isSearching: false });
                    if (loadMore) {
                        dispatch({ type: SET_IS_LOADING_MORE, isLoadingMore: false });
                    } else {
                        tracking.siteSearch(getCategoriesTrackingData(data, words));
                        tracking.siteSearch(getProductsTrackingData(data, words));
                    }
                    return data;
                })
            .catch(e => {
                if (!axios.isCancel(e)) {
                    dispatchError(dispatch, e, {
                        url: search(words, cooperative),
                        description: e,
                    });

                    dispatch({ type: SET_IS_LOADING_MORE, isLoadingMore: false });
                    dispatch({ type: SET_IS_SEARCHING, isSearching: false });
                }
            });
    };
};

/**
 * Populate common categories
 * @return {function(*=, *): Promise<ISearch | never>}
 */
const populateCommonCategories = () => {
    return function(dispatch, getState) {
        const cooperative = getCooperativeBySlug(getState().region.active);
        const term = '';
        return axios
            .get(search(term, cooperative))
            .then(
                /**
                 * @param {ISearch} data
                 * @return {ISearch}
                 */
                ({ data }) => {
                    if (data.products) {
                        data.products.items = productsFactoryMiddleware(data.products.items, { cooperative });
                    }
                    return data;
                })
            .then(
                /**
                 * @param {ISearch} data
                 * @return {ISearch}
                 */
                data => {
                    dispatch({ type: SET_COMMON_CATEGORIES, result: data.common_categories });
                    return data;
                })
            .catch(e => {
                dispatchError(dispatch, e, {
                    url: search(term, cooperative),
                    description: e,
                });
            });
    };
};

/**
 * Load more items
 * @return {Function}
 */
const loadMore = () => {
    return function(dispatch, getState) {
        const { offset, limit, productsTotal, isLoadingMore, term } = getState().search;
        const nextOffset = offset + limit;
        if (nextOffset < productsTotal && !isLoadingMore) {
            dispatch({ type: SET_SEARCH_OFFSET, offset: nextOffset });
            dispatch(query({ term, loadMore: true }));
        }
    };
};

/**
 * Set search term
 * @param {string} term
 * @return {Function}
 */
const persistTerm = ({ term }) => {
    return function(dispatch) {
        dispatch({ type: SET_SEARCH_TERM, term });
    };
};

/**
 * Set limit
 * @param {number} limit
 * @return {Function}
 */
const setLimit = ({ limit }) => {
    return function(dispatch) {
        dispatch({ type: SET_SEARCH_LIMIT, limit });
    };
};

/**
 * Set offset amount
 * @param {number} offset
 * @return {Function}
 */
const setOffset = ({ offset }) => {
    return function(dispatch) {
        dispatch({ type: SET_SEARCH_OFFSET, offset });
    };
};

/**
 * Set fuzzyness in search
 * @param {boolean} isFuzzy
 * @return {Function}
 */
const setFuzzy = ({ isFuzzy }) => {
    return function(dispatch) {
        dispatch({ type: SET_SEARCH_FUZZY, isFuzzy });
    };
};

const open = () => {
    return function(dispatch) {
        dispatch({ type: SET_SEARCH_IS_ACTIVE });
    };
};

const close = () => {
    return function(dispatch) {
        dispatch({ type: SET_SEARCH_IS_INACTIVE });
    };
};

const reset = () => {
    return function(dispatch) {
        dispatch({ type: RESET_SEARCH });
    };
};

export default Object.freeze({
    query,
    loadMore,
    setLimit,
    setOffset,
    open,
    close,
    reset,
    populateCommonCategories,
    setFuzzy,
    persistTerm,
});



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/reducers/modules/search/actions.js